import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Accordion from '../../components/Accordion';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { HelpDeskUserDashboardHelpDeskItem } from '../../api/models';

const HelpDesk: React.FC = () => {
	// Set Color Code
	const helpDeskColorCode = '#A52EBE';

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to hold fetch data
	const [helpDeskData, setHelpDeskData] = React.useState<HelpDeskUserDashboardHelpDeskItem[]>([]);

	const getHelpDeskData = async () => {
		setIsLoading(true);

		const response = await apibridge.getHelpDeskData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setHelpDeskData(response.result);
		} else {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getHelpDeskData();
	}, []);

	// Reorder Data
	const reorderData = (data: HelpDeskUserDashboardHelpDeskItem[]) => {
		const orderedData = data.sort((a, b) => {
			const orderA = a.order || 1;
			const orderB = b.order || -1;
			return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
		});

		setHelpDeskData(orderedData);
	};

	React.useEffect(() => {
		reorderData(helpDeskData);
	}, [helpDeskData]);

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="accordion-general-wrapper">
									<ul className="accordion">
										{helpDeskData && helpDeskData.length > 0 ? (
											helpDeskData.map((item, index) => {
												return (
													<Accordion
														key={item.id}
														heading={item.title}
														content={item.description}
														colorCode={helpDeskColorCode}
														redirectLink={`help-desk/${item.id}`}
													/>
												);
											})
										) : (
											<div className="detail-prompt">
												Information in this section is coming soon. In the meantime feel free to{' '}
												<a href={`${websiteUrl}/contact-us`} target="_blank">
													contact us
												</a>
												.
											</div>
										)}
									</ul>
								</div>

								<ScrollToTopButton extraClass="mtop-16" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default HelpDesk;
